import { RegistrationFieldPermissionsField } from '@stax-core/types-omni';
import { useContext, useCallback } from 'react';
import { RegistrationStore, BrandStore } from '../context';
import { getFieldVisibilityLevel } from './useFieldPermissionFlag';
import { get, flatten, set } from '../util/object.util';

/**
 *
 * This is a wrapper for form validators that returns a
 * new form validator that only return errors for visible fields.
 *
 * This is helpful because to prevent frustrating UX
 * where there is an invalid prefilled (invisible) field that stops
 * the user from proceeding through the signup flow.
 *
 * @param validators one or many normal form validator(s)
 * @returns `validateWriteableFieldFormValues` a form validator that only validates the visible fields
 */
export function useWriteableFieldFormValidation<
  FormValues extends object,
  FormErrors extends object,
>(validateFormValues: (values: FormValues, ...otherArgs: any[]) => FormErrors) {
  const {
    state: { brand },
  } = useContext(BrandStore);

  const {
    state: { registration },
  } = useContext(RegistrationStore);

  const validateWriteableFieldFormValues = useCallback(
    (formValues: FormValues, ...otherArgs: any[]) => {
      const errors = validateFormValues(formValues, ...otherArgs);
      const writeableErrors = {} as FormErrors;

      Object.keys(flatten(errors)).forEach(
        (field: RegistrationFieldPermissionsField) => {
          const canWriteToField =
            getFieldVisibilityLevel(registration, brand, field) === 'write';

          if (canWriteToField) {
            const error = get(errors, field);
            // if we can write to this field, then keep the error
            // i.e. don't try to validate on fields the user can't see lol
            set(writeableErrors, field, error);
          }
        },
      );

      return writeableErrors;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validateFormValues, registration, brand],
  );

  return validateWriteableFieldFormValues;
}
